import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Note, NotePreview } from '@shared/types';
import { apiFetch } from '@/lib/api';
import { useApiToken } from './useApiToken';

const LIST_KEY = (includeArchived: boolean) =>
  ['notes', { includeArchived }] as const;
const ITEM_KEY = (id: string) => ['notes', 'item', id] as const;

export function useNotes(includeArchived = false) {
  const { data: tokenData, isSuccess: tokenReady } = useApiToken();
  return useQuery({
    queryKey: LIST_KEY(includeArchived),
    queryFn: () =>
      apiFetch<NotePreview[]>(
        `/notes${includeArchived ? '?includeArchived=1' : ''}`,
        { token: tokenData?.token },
      ),
    enabled: tokenReady,
  });
}

export function useNote(id: string | null) {
  const { data: tokenData, isSuccess: tokenReady } = useApiToken();
  return useQuery({
    queryKey: ITEM_KEY(id ?? ''),
    queryFn: () => apiFetch<Note>(`/notes/${id}`, { token: tokenData?.token }),
    enabled: tokenReady && !!id,
  });
}

export function useCreateNote() {
  const qc = useQueryClient();
  const { data: tokenData } = useApiToken();
  return useMutation({
    mutationFn: () =>
      apiFetch<Note>('/notes', {
        method: 'POST',
        token: tokenData?.token,
      }),
    onSuccess: () => {
      // Both list views (default + archived) get invalidated — a new
      // note belongs in the default list immediately.
      qc.invalidateQueries({ queryKey: ['notes'] });
    },
  });
}

export interface UpdateNoteInput {
  title?: string;
  body?: string;
  archived?: boolean;
}

export function useUpdateNote() {
  const qc = useQueryClient();
  const { data: tokenData } = useApiToken();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateNoteInput & { id: string }) =>
      apiFetch<Note>(`/notes/${id}`, {
        method: 'PATCH',
        body: patch,
        token: tokenData?.token,
      }),
    // Optimistic patch on the editor query so the title/body stays
    // current while the PATCH is in flight. The list query refetches
    // on settle so preview text updates.
    onMutate: async ({ id, ...patch }) => {
      await qc.cancelQueries({ queryKey: ITEM_KEY(id) });
      const previous = qc.getQueryData<Note>(ITEM_KEY(id));
      if (previous) {
        qc.setQueryData<Note>(ITEM_KEY(id), {
          ...previous,
          ...patch,
          updatedAt: new Date().toISOString(),
        });
      }
      return { previous };
    },
    onError: (_err, { id }, ctx) => {
      if (ctx?.previous) qc.setQueryData(ITEM_KEY(id), ctx.previous);
    },
    onSettled: (_data, _err, { id }) => {
      qc.invalidateQueries({ queryKey: ITEM_KEY(id) });
      qc.invalidateQueries({ queryKey: ['notes'] });
    },
  });
}

export function useDeleteNote() {
  const qc = useQueryClient();
  const { data: tokenData } = useApiToken();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ id: string }>(`/notes/${id}`, {
        method: 'DELETE',
        token: tokenData?.token,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notes'] }),
  });
}
